import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save } from "lucide-react";
import { toast } from "sonner";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";

const FIELDS = [
  { key: "name", label: "Business name", placeholder: "Three Zee Agro Tech", full: false },
  { key: "tagline", label: "Tagline", placeholder: "Agricultural Machinery Sales & Service", full: false },
  { key: "phone", label: "Phone", placeholder: "+91 98765 43210", full: false },
  { key: "email", label: "Email", placeholder: "contact@threezeeagrotech.com", full: false },
  { key: "website", label: "Website", placeholder: "threezeeagrotech.com", full: false },
  { key: "gstin", label: "GSTIN", placeholder: "22AAAAA0000A1Z5", full: false },
  { key: "address_line1", label: "Address line 1", placeholder: "Main Highway, Plot 12", full: true },
  { key: "address_line2", label: "Address line 2", placeholder: "Near Krishi Mandi", full: true },
  { key: "city", label: "City", placeholder: "Bhubaneswar", full: false },
  { key: "state", label: "State", placeholder: "Odisha", full: false },
  { key: "pincode", label: "PIN code", placeholder: "751001", full: false },
];

export default function BusinessProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/business-profile");
    setForm(data);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  if (!form) return <SkeletonCard lines={5} />;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/business-profile", form);
      toast.success("Business profile saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 lg:p-10 max-w-4xl">
      <PageHeader
        title="Business Profile"
        subtitle="Contact details that appear on invoices, receipts and PDF reports. Update once — it flows everywhere."
        testid="page-business-profile"
      />

      <form onSubmit={save} className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-6" data-testid="business-profile-form">
        <div className="flex items-center gap-3 pb-4 mb-4 border-b border-[#3A4640]">
          <div className="w-14 h-14 rounded-md bg-[#121815] border border-[#3A4640] p-1 flex items-center justify-center">
            <img src="/logo.jpg" alt="Three Zee Agro Tech" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-heading font-bold text-[#F8F9FA]">Three Zee Agro Tech</div>
            <div className="text-xs text-[#8A8F98]">Edit contact information shown to customers.</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.full ? "col-span-2" : ""}>
              <Label htmlFor={`bp-${f.key}`}>{f.label}</Label>
              <Input
                id={`bp-${f.key}`}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                data-testid={`bp-${f.key}-input`}
              />
            </div>
          ))}
          <div className="col-span-2">
            <Label htmlFor="bp-footer-note">Invoice footer note</Label>
            <Textarea
              id="bp-footer-note"
              rows={2}
              value={form.footer_note || ""}
              onChange={(e) => setForm({ ...form, footer_note: e.target.value })}
              placeholder="Thank you for choosing Three Zee Agro Tech. Warranty applies as per manufacturer terms."
              data-testid="bp-footer-note-input"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="text-xs text-[#8A8F98]">
            {form.updated_at ? (
              <>Last updated {formatDate(form.updated_at)} by {form.updated_by || "-"}</>
            ) : (
              "Not customized yet."
            )}
          </div>
          <Button type="submit" disabled={busy} data-testid="bp-save-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
            <Save size={14} className="mr-2" /> Save profile
          </Button>
        </div>
      </form>
    </div>
  );
}
